import * as React from 'react';
import { useModal } from '@blueprint/ui';
import { Icon, ModalDrawer } from '@blueprint/ui';
import {
    MobileOptionsButton,
    ShareSmallColored,
    EmailOutlineSmallColored,
    BinOutlineColored
} from './MenuShareModalButton.styles';
import {
    ListingSharing,
    ShareLinkButton,
    ShareList,
    ShareListItem
} from '@zoopla/listings/share';
import { ListingUris, PublicationStatus } from '../../graphql.generated';

type ListingCardMiniMenuButtonProps = {
    testId: string;
    trackOpenMenuModal: () => void;
    trackCloseMenuModal: () => void;
    trackOpenShareModal: () => void;
    trackCloseShareModal: () => void;
    trackContactAgentClick: (listingId: string) => void;
    deleteListing: () => void;
    listingId: string;
    title: string;
    publicationStatus: PublicationStatus;
    listingUris: ListingUris;
    contactLink: string;
};

export const ListingCardMiniMenuButton = ({
    testId,
    trackOpenMenuModal,
    trackCloseMenuModal,
    trackOpenShareModal,
    trackCloseShareModal,
    trackContactAgentClick,
    deleteListing,
    listingId,
    title,
    publicationStatus,
    listingUris,
    contactLink
}: ListingCardMiniMenuButtonProps) => {
    const onCloseMenuModal = () => {
        trackCloseMenuModal();
    };

    const onCloseShareModal = () => {
        trackCloseShareModal();
        if (menuModalIsShowing) {
            closeMenuModal();
        }
    };

    const [menuModalIsShowing, _openMenuModal, closeMenuModal] = useModal({
        onClose: onCloseMenuModal
    });
    const [shareModalIsShowing, _openShareModal, closeShareModal] = useModal({
        onClose: onCloseShareModal
    });

    const openMenuModal = () => {
        _openMenuModal(null);
        trackOpenMenuModal();
    };

    const openShareModal = () => {
        _openShareModal(null);
        trackOpenShareModal();
    };

    const shareModal = (
        <ModalDrawer
            isShowing={shareModalIsShowing}
            close={(event?: React.MouseEvent<HTMLElement>) => {
                event?.stopPropagation();
                closeShareModal();
            }}
        >
            <ListingSharing
                listingId={listingId}
                title={title}
                listingUrl={listingUris.detail || ''}
                sharingCategory="User Account"
            />
        </ModalDrawer>
    );

    const mobileMenuModal = (
        <ModalDrawer
            isShowing={menuModalIsShowing}
            close={(event?: React.MouseEvent<HTMLElement>) => {
                event?.stopPropagation();
                closeMenuModal();
            }}
        >
            <ShareList>
                {publicationStatus === PublicationStatus.Expired ? null : (
                    <>
                        <ShareListItem>
                            <ShareLinkButton
                                onClick={(
                                    event: React.MouseEvent<HTMLElement>
                                ) => {
                                    event.stopPropagation();
                                    openShareModal();
                                }}
                                data-testid={`mobile-menu-share-button-${listingId}`}
                            >
                                <ShareSmallColored />
                                <span>Share</span>
                            </ShareLinkButton>
                        </ShareListItem>
                        <ShareListItem>
                            <ShareLinkButton
                                href={contactLink}
                                onClick={(
                                    event: React.MouseEvent<HTMLElement>
                                ) => {
                                    event.stopPropagation();
                                    trackContactAgentClick(listingId);
                                }}
                            >
                                <EmailOutlineSmallColored />
                                <span>Contact agent</span>
                            </ShareLinkButton>
                        </ShareListItem>
                    </>
                )}
                <ShareListItem>
                    <ShareLinkButton
                        data-testid={`delete-button-${listingId}`}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            event.stopPropagation();
                            deleteListing();
                        }}
                    >
                        <BinOutlineColored />
                        <span>Remove from saved</span>
                    </ShareLinkButton>
                </ShareListItem>
            </ShareList>
        </ModalDrawer>
    );

    return (
        <>
            <MobileOptionsButton
                variant="tertiary"
                iconOnly
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openMenuModal();
                }}
                data-testid={testId}
            >
                <Icon iconId="menuMore" />
            </MobileOptionsButton>

            {mobileMenuModal}
            {shareModal}
        </>
    );
};
